import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';

import { api } from '../../urlConfig';
import {
  getSocket,
  getSocketClientId,
  reconnectSocketUser,
} from '../../socket/socket';

const HEARTBEAT_MS = 15000;
const ACTIVITY_EMIT_MS = 10000;
const BLOCK_CHECK_MS = 10000;
const BLOCK_STORAGE_KEY = 'system:account-blocked:v1';
const VISITOR_ID_KEY = 'alotb:visitor-id:v1';


const getVisitorId = () => {
  try {
    const stored = window.localStorage.getItem(VISITOR_ID_KEY);
    if (stored) return stored;
    const next = typeof window.crypto?.randomUUID === 'function'
      ? window.crypto.randomUUID()
      : `visitor_${Date.now()}_${Math.random().toString(36).slice(2, 14)}`;
    window.localStorage.setItem(VISITOR_ID_KEY, next);
    return next;
  } catch (_error) {
    return `visitor_${Date.now()}_${Math.random().toString(36).slice(2, 14)}`;
  }
};

const detectBrowser = () => {
  if (typeof navigator === 'undefined') return '';
  const ua = String(navigator.userAgent || '');
  if (/Edg\//i.test(ua)) return 'Edge';
  if (/OPR\//i.test(ua)) return 'Opera';
  if (/Firefox\//i.test(ua)) return 'Firefox';
  if (/Chrome\//i.test(ua)) return 'Chrome';
  if (/Safari\//i.test(ua)) return 'Safari';
  return 'Browser';
};

const detectDevice = () => {
  if (typeof navigator === 'undefined') return '';
  const ua = String(navigator.userAgent || '');
  const platform = String(navigator.userAgentData?.platform || navigator.platform || '');
  if (/Android/i.test(ua)) return 'Android';
  if (/iPhone/i.test(ua)) return 'iPhone';
  if (/iPad/i.test(ua) || (/Mac/i.test(platform) && Number(navigator.maxTouchPoints || 0) > 1)) return 'iPad';
  if (/Windows/i.test(ua) || /Win/i.test(platform)) return 'Windows PC';
  if (/Mac/i.test(ua) || /Mac/i.test(platform)) return 'Mac';
  if (/Linux/i.test(ua) || /Linux/i.test(platform)) return 'Linux';
  return platform || 'Device';
};

const readBlocked = () => {
  try {
    const raw = window.localStorage.getItem(BLOCK_STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch (_error) {
    return null;
  }
};

const saveBlocked = (value) => {
  try {
    if (value) window.localStorage.setItem(BLOCK_STORAGE_KEY, JSON.stringify(value));
    else window.localStorage.removeItem(BLOCK_STORAGE_KEY);
  } catch (_error) { }
};

const SystemPresenceBridge = () => {
  const location = useLocation();
  const routeRef = useRef('');
  const emitRef = useRef(null);
  const lastActivityEmitRef = useRef(0);
  const [blocked, setBlocked] = useState(readBlocked);
  const [checking, setChecking] = useState(false);

  routeRef.current = `${location.pathname || '/'}${location.search || ''}`;

  const applyBlocked = useCallback((payload = {}) => {
    const next = {
      message: payload.message || 'Доступ к аккаунту заблокирован администратором.',
      reason: payload.reason || '',
      blockedAt: payload.blockedAt || new Date().toISOString(),
    };
    saveBlocked(next);
    setBlocked(next);
  }, []);

  const checkAccess = useCallback(async () => {
    const token = window.localStorage.getItem('token') || '';
    if (!token) return false;

    setChecking(true);
    try {
      const response = await fetch(`${api}/system/access-status`, {
        method: 'GET',
        headers: { Authorization: `Bearer ${token}` },
        cache: 'no-store',
      });
      if (!response.ok) return false;
      const data = await response.json();
      if (data?.blocked === false) {
        saveBlocked(null);
        setBlocked(null);
        reconnectSocketUser();
        return true;
      }
      if (data?.blocked === true && !blocked) applyBlocked(data);
      return false;
    } catch (_error) {
      return false;
    } finally {
      setChecking(false);
    }
  }, [applyBlocked, blocked]);

  useEffect(() => {
    const socket = getSocket();
    const clientId = getSocketClientId();
    const visitorId = getVisitorId();
    const deviceLabel = detectDevice();
    const browserLabel = detectBrowser();
    let heartbeatTimer = null;

    const emitPresence = ({ activity = false } = {}) => {
      if (!socket?.connected || blocked) return;
      socket.emit('system:presence', {
        visitorId,
        clientId,
        route: routeRef.current,
        visibility: typeof document !== 'undefined' && document.hidden ? 'hidden' : 'visible',
        deviceLabel,
        browserLabel,
        activity,
      });
    };

    emitRef.current = emitPresence;
    const onConnect = () => emitPresence({ activity: true });
    const onBlocked = (payload) => applyBlocked(payload);
    const onConnectError = (error) => {
      if (error?.data?.code === 'USER_BLOCKED') applyBlocked({});
    };
    const onVisibility = () => emitPresence({ activity: false });
    const onActivity = () => {
      const timestamp = Date.now();
      if (timestamp - lastActivityEmitRef.current < ACTIVITY_EMIT_MS) return;
      lastActivityEmitRef.current = timestamp;
      emitPresence({ activity: true });
    };

    socket.on('connect', onConnect);
    socket.on('account:blocked', onBlocked);
    socket.on('connect_error', onConnectError);
    if (socket.connected) emitPresence({ activity: true });

    heartbeatTimer = window.setInterval(() => emitPresence({ activity: false }), HEARTBEAT_MS);
    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('pointerdown', onActivity, { passive: true });
    window.addEventListener('keydown', onActivity, { passive: true });
    window.addEventListener('scroll', onActivity, { passive: true });

    return () => {
      emitRef.current = null;
      socket.off('connect', onConnect);
      socket.off('account:blocked', onBlocked);
      socket.off('connect_error', onConnectError);
      if (heartbeatTimer) window.clearInterval(heartbeatTimer);
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('pointerdown', onActivity);
      window.removeEventListener('keydown', onActivity);
      window.removeEventListener('scroll', onActivity);
    };
  }, [applyBlocked, blocked]);

  useEffect(() => {
    if (!blocked) emitRef.current?.({ activity: false });
  }, [blocked, location.pathname, location.search]);

  useEffect(() => {
    if (!blocked) return undefined;
    const timer = window.setInterval(checkAccess, BLOCK_CHECK_MS);
    return () => window.clearInterval(timer);
  }, [blocked, checkAccess]);

  if (!blocked) return null;

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 2147483647, display: 'grid', placeItems: 'center', padding: 24, background: 'rgba(7,10,18,.96)', color: '#f8fafc' }}>
      <div style={{ width: 'min(560px, 100%)', padding: 30, borderRadius: 22, border: '1px solid rgba(248,113,113,.4)', background: '#111827', boxShadow: '0 30px 80px rgba(0,0,0,.45)', textAlign: 'center' }}>
        <div style={{ fontSize: 42, marginBottom: 12 }}>⛔</div>
        <h1 style={{ margin: '0 0 12px', fontSize: 26 }}>Доступ к аккаунту заблокирован</h1>
        <p style={{ margin: '0 0 10px', color: '#cbd5e1', lineHeight: 1.55 }}>{blocked.message}</p>
        {blocked.reason ? <p style={{ margin: '0 0 18px', color: '#fca5a5' }}>Причина: {blocked.reason}</p> : null}
        <button type="button" disabled={checking} onClick={checkAccess} style={{ minHeight: 44, padding: '0 18px', border: 0, borderRadius: 12, cursor: checking ? 'wait' : 'pointer', fontWeight: 800 }}>
          {checking ? 'Проверяю…' : 'Проверить доступ'}
        </button>
        <div style={{ marginTop: 12, color: '#64748b', fontSize: 12 }}>После разблокировки доступ восстановится автоматически.</div>
      </div>
    </div>
  );
};

export default SystemPresenceBridge;
